// Shared click + keyboard props for non-button openable surfaces (the
// .hoverable-tile / .hoverable-row affordance contract). Mirrors the inline
// pattern locked in EquipmentFleetView/WeighInSessionListTile: role="button",
// tabIndex={0}, and Enter/Space activation, so keyboard users get the same
// whole-element action as mouse users.
//
// The e.target !== e.currentTarget guard keeps Enter/Space on a nested
// control (Clear/Edit buttons inside a row) from also firing the row open;
// nested controls own their own activation. Click bubbling from nested
// controls is each call site's job (stopPropagation on the nested control).
import type {KeyboardEvent, MouseEvent} from 'react';

// The two ways a surface is opened. `open` is called with the click event by
// React and with the keydown event by the handler below, so it must accept
// either — every measured call site ignores the argument entirely.
export type OpenableEvent = MouseEvent<Element> | KeyboardEvent<Element>;

export interface OpenableProps {
  onClick: (event: MouseEvent<Element>) => void;
  // Literal, not `string`: these props are spread onto a real element, and a
  // widened role would not be assignable to it.
  role: 'button';
  tabIndex: number;
  onKeyDown: (event: KeyboardEvent<Element>) => void;
}

export function openableProps(open: (event: OpenableEvent) => void): OpenableProps {
  return {
    onClick: open,
    role: 'button',
    tabIndex: 0,
    onKeyDown: (e) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        open(e);
      }
    },
  };
}
